import React from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';
import {
  FDBodyText,
  FDList,
  FDListItem,
  FDListItemText,
  FDPaper,
  FDSecondaryActionButton,
  FDStack,
} from '@facet-wealth/design';
import Popover from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Popover';
import usePopoverState from 'Hooks/usePopoverState';
import { displayErrorMessageWithChecks } from 'Context/SnackbarContext/utils';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { dataAttrs } from 'Helpers/Testing';
import { navigateByRelationship } from 'Views/Page/Content/Templates';
import { useLayoutContext } from 'Context/LayoutContext';

const handleClickProfilePage = (rel, loadModalCollectionByLink, loadPageContentLink, clearModalCollection, forceFocusMode, closePopover) => {
  return async () => {
    await navigateByRelationship(rel, loadModalCollectionByLink, loadPageContentLink, clearModalCollection, { forceFocusMode });
    closePopover();
  };
};

const handleClickRefresh = (rel, setLoading, setSaving, refreshPageContentCollection, displayErrorMessage) => {
  return async () => {
    setLoading(true);
    setSaving(true);
    try {
      await rel.link('related').post({}, {
        deprecatedAllowAlterOfRelation: true,
      });
      await refreshPageContentCollection();
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to update roadmap with member profile data');
    } finally {
      setLoading(false);
      setSaving(false);
    }
  };
};

const handleClickUpdate = () => {
  return async () => {
    // TODO
  };
};

const handleClickClearAll = (rel, setLoading, setSaving, refreshPageContentCollection, displayErrorMessage) => {
  return async () => {
    setLoading(true);
    setSaving(true);
    try {
      await rel.link('related').delete();
      await refreshPageContentCollection();
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to reset this path');
    } finally {
      setLoading(false);
      setSaving(false);
    }
  };
};

const LeversSidebarAdvancedOptionsPopoverButton = ({ resource, setLoading, setSaving }) => {
  const {
    open,
    anchorEl,
    openPopover,
    handleClose,
  } = usePopoverState({});
  const { displayErrorMessage } = useSnackbarContext();
  const { isFocusedLayout } = useLayoutContext();
  const {
    loadModalCollectionByLink, loadPageContentLink, clearModalCollection, refreshPageContentCollection,
  } = usePageContentContext();

  const profilePageRel = resource.withRelationship(
    fr.relationships.profilePage.name,
    (rel) => rel,
    () => null,
  );
  const refreshRel = resource.withRelationship(
    fr.relationships.refresh.name,
    (rel) => rel,
    () => null,
  );
  const updateRel = resource.withRelationship(
    fr.relationships.update.name,
    (rel) => rel,
    () => null,
  );
  const clearAllRel = resource.withRelationship(
    fr.relationships.reset.name,
    (rel) => rel,
    () => null,
  );

  const onClickProfilePage = handleClickProfilePage(profilePageRel, loadModalCollectionByLink, loadPageContentLink, clearModalCollection, isFocusedLayout, handleClose);
  const onClickRefresh = handleClickRefresh(refreshRel, setLoading, setSaving, refreshPageContentCollection, displayErrorMessage);
  const onClickUpdate = handleClickUpdate(); // TODO: Implement update
  const onClickClearAll = handleClickClearAll(clearAllRel, setLoading, setSaving, refreshPageContentCollection, displayErrorMessage);

  const relNames = [
    fr.relationships.profilePage.name,
    fr.relationships.refresh.name,
    fr.relationships.update.name,
    fr.relationships.reset.name,
  ];

  const relNameToRel = {
    profilePage: profilePageRel,
    refresh: refreshRel,
    update: updateRel,
    reset: clearAllRel,
  };

  const relNameToOnClick = {
    profilePage: onClickProfilePage,
    refresh: onClickRefresh,
    update: onClickUpdate,
    reset: onClickClearAll,
  };

  const hasAnyOptions = !!clearAllRel;
  if (!hasAnyOptions) {
    return null;
  }

  return (
    <>
      <FDSecondaryActionButton
        onClick={openPopover}
        label="Advanced options"
        {...dataAttrs('roadmap-explorer-levers-adv-opts-btn')}
      />
      <Popover
        id={'roadmap-explorer-levers-adv-opts-popover'}
        rounded
        open={open}
        anchorEl={anchorEl}
        onClose={(event) => {
          event.stopPropagation();
          handleClose();
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <FDPaper appearance={'elevated'} padding={2} minWidth={350} overflow={'auto'}>
          <FDStack direction={'column'} justifyContent={'flex-start'}>
            <FDList>
              {relNames.map((key) => {
                return relNameToRel[key] && (
                <FDListItem onClick={relNameToOnClick[key]}>
                  <FDListItemText
                    primaryText={<FDBodyText size={'lg'} value={relNameToRel[key].meta('prompt')} color={'textSecondary'} />}
                  />
                </FDListItem>
                );
              })}
            </FDList>
          </FDStack>
        </FDPaper>
      </Popover>
    </>
  );
};

LeversSidebarAdvancedOptionsPopoverButton.propTypes = {
  resource: PropTypes.object.isRequired,
  setLoading: PropTypes.func.isRequired,
  setSaving: PropTypes.func.isRequired,
};

export default LeversSidebarAdvancedOptionsPopoverButton;
